import { useQuery, queryOptions } from '@tanstack/react-query';
import { apiClient } from '../client';
import { authKeys } from '../query-keys';
import { useAuthStore } from '@/stores/auth-store';
import { listMembershipsContract, type ListMembershipsResponse } from '@bliks-io/contracts';

/**
 * Standalone query options for the caller's workspace memberships.
 * Used directly by the SSO callback (`queryClient.fetchQuery`) so the
 * select-workspace screen renders from a prewarmed cache.
 */
export const membershipsQueryOptions = queryOptions({
  queryKey: authKeys.memberships(),
  queryFn: async ({ signal }): Promise<ListMembershipsResponse> => {
    const { data } = await apiClient.get<ListMembershipsResponse>(listMembershipsContract.path, {
      signal
    });
    return data;
  }
});

/**
 * Fetch the workspaces the signed-in identity can enter.
 *
 * Legacy password sessions carry no identity link and get an empty list from
 * the backend, so multi-workspace UI (picker, switcher) stays hidden for them
 * without any client-side special casing.
 */
export function useMemberships() {
  const { token } = useAuthStore();

  const query = useQuery({
    ...membershipsQueryOptions,
    enabled: !!token
  });

  return {
    ...query,
    memberships: query.data?.memberships ?? []
  };
}
